import Encoder from './encoder';

const processorCode = `
class AudioRecorderProcessor extends AudioWorkletProcessor {
   process(inputs) {
      const input = inputs[0];
      if (input && input.length > 0) {
         this.port.postMessage(input[0]);
      }
      return true;
   }
}
registerProcessor('audio-recorder-processor', AudioRecorderProcessor);
`;

const processorBlob = new Blob([processorCode], { type: 'application/javascript' });
const processorURL = URL.createObjectURL(processorBlob);

let _sharedContext = null;
let _modulePromise = null;

function getSharedContext() {
   const AudioCtx = window.AudioContext || window.webkitAudioContext;

   if (!_sharedContext || _sharedContext.state === 'closed') {
      _sharedContext = new AudioCtx();
      _modulePromise = _sharedContext.audioWorklet.addModule(processorURL);
   }

   return { context: _sharedContext, modulePromise: _modulePromise };
}

class MicRecorder {
   constructor(config) {
      this.config = {
         bitRate: 128,
         startRecordingAt: 50, // ms to skip initial mic-open noise
         deviceId: null,
      };

      this.context = null;
      this.microphone = null;
      this.processor = null;
      this.activeStream = null;
      this.timerToStart = null;
      this.isPaused = false;

      Object.assign(this.config, config);
   }

   /**
    * Pre-warm the AudioContext and load the AudioWorklet module.
    * Call this before the user clicks record (e.g., on component mount).
    * Subsequent calls are no-ops — the promise is cached.
    */
   static prewarm() {
      if (typeof window !== 'undefined') getSharedContext();
   }

   /**
    * Connects the microphone stream to the AudioWorkletNode for encoding.
    * Module loading is skipped if already pre-warmed (instant).
    * @param {MediaStream} stream
    */
   async addMicrophoneListener(stream) {
      this.activeStream = stream;

      this.timerToStart = setTimeout(() => {
         this.timerToStart = null;
      }, this.config.startRecordingAt);

      await _modulePromise;

      this.microphone = this.context.createMediaStreamSource(stream);
      this.processor = new AudioWorkletNode(this.context, 'audio-recorder-processor');

      this.processor.port.onmessage = event => {
         if (this.timerToStart) return;
         this.lameEncoder.encode(event.data);
      };

      this.microphone.connect(this.processor);
   }

   /**
    * Pause recording.
    * AudioContext.suspend() halts the entire audio pipeline — the worklet stops
    * firing postMessage events entirely, eliminating CPU overhead while paused.
    */
   pause() {
      if (this.context && this.context.state === 'running') {
         this.context.suspend();
         this.isPaused = true;
      }
      return this;
   }

   /**
    * Stop all mic tracks — removes browser recording indicator.
    * The audio pipeline (processor + encoder) stays intact for resume.
    */
   stopTracks() {
      if (this.activeStream) {
         this.activeStream.getAudioTracks().forEach(track => track.stop());
      }
   }

   /**
    * Swap the dead mic source with a fresh stream.
    * Called on resume after tracks were stopped on pause.
    * Preserves the AudioWorklet processor and encoder buffer.
    * @param {MediaStream} newStream
    */
   swapStream(newStream) {
      if (this.microphone) {
         this.microphone.disconnect();
      }
      this.activeStream = newStream;
      this.microphone = this.context.createMediaStreamSource(newStream);
      this.microphone.connect(this.processor);
   }

   /**
    * Resume recording.
    */
   resume() {
      if (this.context && this.context.state === 'suspended') {
         this.context.resume();
         this.isPaused = false;
      }
      return this;
   }

   /**
    * Stop recording and disconnect all audio nodes.
    * Does NOT close the shared AudioContext — it is reused for the next recording.
    */
   stop() {
      clearTimeout(this.timerToStart);
      this.timerToStart = null;

      if (this.processor) {
         this.processor.port.onmessage = null;
         this.processor.disconnect();
         this.processor = null;
      }

      if (this.microphone) {
         this.microphone.disconnect();
         this.microphone = null;
      }

      if (this.activeStream) {
         this.activeStream.getAudioTracks().forEach(track => track.stop());
         this.activeStream = null;
      }

      this.isPaused = false;

      return this;
   }

   /**
    * Request microphone access and begin recording.
    * Uses the pre-warmed shared AudioContext — no compilation delay after first use.
    * @returns {Promise<MediaStream>}
    */
   async start() {
      const { context, modulePromise } = getSharedContext();
      this.context = context;
      _modulePromise = modulePromise;

      // Resume if the browser auto-suspended the idle context
      if (this.context.state === 'suspended') {
         await this.context.resume();
      }

      this.config.sampleRate = this.context.sampleRate;
      this.lameEncoder = new Encoder(this.config);
      this.isPaused = false;

      const audioConstraints = this.config.deviceId ? { deviceId: { exact: this.config.deviceId } } : true;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      await this.addMicrophoneListener(stream);
      return stream;
   }

   /**
    * Finalize encoding and return the MP3 buffer and blob.
    * @returns {Promise<[Float32Array[], Blob]>}
    */
   getMp3() {
      const finalBuffer = this.lameEncoder.finish();

      return new Promise((resolve, reject) => {
         if (finalBuffer.length === 0) {
            reject(new Error('No audio buffer to encode'));
         } else {
            const blob = new Blob(finalBuffer, { type: 'audio/mp3' });
            this.lameEncoder.clearBuffer();
            resolve([finalBuffer, blob]);
         }
      });
   }
}

export default MicRecorder;
